import apiService from '../../../services/apiService';
import axios from 'axios';

const TIFFIN_API = '/ykrestapi/Tiffin';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const PAYMENT_API = '/ykrestapi/PaymentChannel';
const MESSAGE_API = '/ykrestapi/MessageChannel';

export const tiffinOrderService = {
  // GET /ykrestapi/tiffin/active-menu
  getActiveMenu: async (menuDate = null, orgId = 3) => {
    try {
      let url = `${TIFFIN_API}/active-menu?orgId=${orgId}`;
      if (menuDate) {
        url += `&menuDate=${menuDate}`;
      }
      const response = await apiService.get(url);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching active menu:', error);
      throw error;
    }
  },

  // POST /ykrestapi/tiffin/create-order
  createOrder: async (orderData) => {
    try {
      const response = await apiService.post(`${TIFFIN_API}/create-order`, orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating order:', error);
      throw error;
    }
  },

  // POST /ykrestapi/tiffin/confirm-payment
  confirmPayment: async (paymentData) => {
    try {
      const response = await apiService.post(`${TIFFIN_API}/confirm-payment`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Error confirming payment:', error);
      throw error;
    }
  },

  // POST /ykrestapi/tiffin/create-kitchen-order  (Sita Rosi — saves to Kitchen_Order table)
  createKitchenOrder: async (orderData) => {
    try {
      const response = await apiService.post(`${TIFFIN_API}/create-kitchen-order`, orderData);
      return response.data;
    } catch (error) {
      console.error('Error creating kitchen order:', error);
      throw error;
    }
  },

  // POST /ykrestapi/tiffin/confirm-kitchen-payment
  confirmKitchenPayment: async (paymentData) => {
    try {
      const response = await apiService.post(`${TIFFIN_API}/confirm-kitchen-payment`, paymentData);
      return response.data;
    } catch (error) {
      console.error('Error confirming kitchen payment:', error);
      throw error;
    }
  },

  // GET /ykrestapi/tiffin/order/{orderId}
  getOrderDetail: async (orderId) => {
    try {
      const response = await apiService.get(`${TIFFIN_API}/order/${orderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching order detail:', error);
      throw error;
    }
  },

  // Process payment via existing AuthNet endpoint (reuses PaymentChannel)
  processPayment: async (paymentDetails) => {
    try {
      const isACH = paymentDetails.paymentType === 'ACH';

      const transformedData = {
        app: "Tiffin",
        entity: "RMS",
        paymentType: isACH ? "ach" : "cc",
        customerEmail: paymentDetails.email,
        orderid: paymentDetails.orderId?.toString() || "0",
        amount: paymentDetails.amount?.toString() || "0",
        description: `Tiffin Order #${paymentDetails.orderId} - Amount: $${paymentDetails.amount}`,
        customer: {
          firstName: paymentDetails.firstName || '',
          lastName: paymentDetails.lastName || '',
          email: paymentDetails.email,
          phone: paymentDetails.phone || '',
          company: "string",
          notes: `Tiffin Order for ${paymentDetails.firstName} ${paymentDetails.lastName}`,
          customerAccount: paymentDetails.userId?.toString() || "string",
          shippingSameAsBilling: true,
          billingAddress: {
            streetAddress1: paymentDetails.streetAddress1 || "-",
            streetAddress2: paymentDetails.streetAddress2 || "-",
            city: paymentDetails.city || "-",
            stateCode: paymentDetails.stateCode || "-",
            zipCode: paymentDetails.zipCode || "-",
            country: paymentDetails.country || "USA"
          },
          shippingAddress: {
            streetAddress1: paymentDetails.streetAddress1 || "-",
            streetAddress2: paymentDetails.streetAddress2 || "-",
            city: paymentDetails.city || "-",
            stateCode: paymentDetails.stateCode || "-",
            zipCode: paymentDetails.zipCode || "-",
            country: paymentDetails.country || "USA"
          }
        },
        creditcard: isACH ? {
          creditCardNumber: "string",
          expirationDate: "string",
          issuer: "string",
          billingZipCode: "string",
          customerId: "",
          cvv: "string"
        } : {
          creditCardNumber: paymentDetails.cardNumber,
          expirationDate: paymentDetails.expiryDate,
          issuer: "string",
          billingZipCode: paymentDetails.zipCode || "-",
          customerId: "",
          cvv: paymentDetails.cvv
        },
        ach: isACH ? {
          isCheckingAccount: paymentDetails.accountType === 'checking' ? "Y" : "N",
          routingNumber: paymentDetails.routingNumber,
          accountNumber: paymentDetails.accountNumber,
          bankName: paymentDetails.bankName || "Financial Institution",
          customerId: "",
          isDefault: "false"
        } : {
          isCheckingAccount: "string",
          routingNumber: "string",
          accountNumber: "string",
          bankName: "string",
          customerId: "string",
          isDefault: "string"
        },
        recurr: {
          paymentAmount: "string",
          accountId: "string",
          startDate: "string",
          executionFrequencyType: "string",
          executionFrequencyParameter: "string",
          endDate: "string",
          paymentSubType: "string",
          invoiceNumber: "string",
          firstPaymentAmount: "string",
          firstPaymentDate: "string",
          description: "string"
        }
      };

      const response = await axios.post(`${API_BASE_URL}${PAYMENT_API}/AuthNetOneTimePayment`, transformedData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data;
    } catch (error) {
      console.error('Tiffin payment processing error:', error?.response?.data || error);
      throw error;
    }
  },

  // Send confirmation email via existing MessageChannel
  sendConfirmationEmail: async (emailData) => {
    try {
      const emailParameter = {
        emailFrom: '',
        emailTo: emailData.toEmail || '',
        emailSubject: '',
        emailBody: '',
        attachment: 'Y',
        userPhoneNumber: '',
        location: 'YKTiffinPayment',
        input: '',
        appContent: {
          userName: emailData.customerName || '',
          programName: 'Tiffin Order',
          appSchedule: '',
          attendee: '',
          studentNames: '',
          date: emailData.transactionDate || new Date().toLocaleString(),
          orderType: 'Tiffin Order',
          orderNo: emailData.transactionId || '',
          paymentMode: emailData.paymentMethod || '',
          amount: `$${parseFloat(emailData.totalAmount || 0).toFixed(2)}`,
          email: emailData.toEmail || '',
          phone: emailData.phone || '',
          pickupPerson: emailData.customerName || '',
          pickupPersonContact: emailData.phone || '',
          instructions: emailData.notes || '',
          itemData: emailData.itemsSummary || '',
          pickUpDate: emailData.pickupDate || new Date().toLocaleDateString(),
          pickUpTime: emailData.pickupTime || '',
          paymentType: emailData.paymentMethod || '',
          billingAddress: '',
          address: 'Yugal Kunj - Radha Krishna Temple and Community Center, 2769 Duluth Hwy, Duluth, GA 30096',
          pickupOrReceiver: 'Pickup Person',
          orderReady: `$${parseFloat(emailData.totalAmount || 0).toFixed(2)}`,
          orderReadyLastLine: '',
          headingDate: 'Pickup Date',
          headingTime: 'Pickup Time',
          startDate: new Date().toISOString(),
          endDate: new Date().toISOString()
        }
      };

      const response = await axios.post(
        `${API_BASE_URL}${MESSAGE_API}/SendEmailMessage?appName=Tiffin&templateName=YKTiffinPayment&orgId=3`,
        emailParameter
      );
      return response.data;
    } catch (error) {
      console.error('Tiffin email sending error:', error?.response?.data || error);
      throw error;
    }
  },

  // Bank name lookup (reuses existing endpoint)
  lookupBankName: async (routingNumber) => {
    try {
      const response = await fetch(`${API_BASE_URL}/ykrestapi/CommonChannel/GetBankNameByRoutingNumber?routingNumber=${routingNumber}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        return data.bankName || data.name || data.institutionName || '';
      }
      return '';
    } catch (error) {
      console.error('Bank lookup error:', error);
      return '';
    }
  },
};

export default tiffinOrderService;
